import { getDefaultTrenchSettings, } from "../../settings/earthbrain-site-settings";
import { ConstructionState, Trench } from "../../site/trench/earthbrain-trench";
import { ERubbingDestination, } from "../../site/trench/types";
import { TrenchEditHandler } from "./trench-edit-handler";
import { TrenchConstructionHandler } from "./trench-construction-handler";
import { VolumeCalculationUtils } from "../../utilities/volume-calculation-utils";
import { EarthBrainUtils } from "../../utilities/earthbrainutility";
import { Activity, ActivityLogger } from "../../utilities/activity-logger";
import { AssetType } from "../../types";
export class TrenchHandler {
    _trenchConstructionHandler;
    _trenchEditHandler;
    _site;
    _cesiumViewer;
    get editHandler() {
        return this._trenchEditHandler;
    }
    constructor(args, cesiumViewer) {
        this._site = args.site;
        this._trenchConstructionHandler = new TrenchConstructionHandler(args, cesiumViewer);
        this._trenchEditHandler = new TrenchEditHandler(args, cesiumViewer);
        this._cesiumViewer = cesiumViewer;
    }
    //For JSON data format refer file link: https://cctinc01.sharepoint.com/sites/nCircle-Orizuru/_layouts/15/download.aspx?UniqueId=f87226d610f24c01af5138cc6fe6da15&e=e2oNqQ
    //TRENCH : TO BE REVIEWED
    async createTrenchFromPoints(controlPointArray, associatedTerrain, associatedRubbingDestination) {
        const trench = await Trench.FromControlPoints(controlPointArray, getDefaultTrenchSettings(), this._cesiumViewer, associatedTerrain, associatedRubbingDestination);
        this._site.addTrench(trench);
        return trench;
    }
    startTrenchConstruction(callback, associatedTerrain, associatedRubbingDestination) {
        this._trenchConstructionHandler.start(callback, associatedTerrain, associatedRubbingDestination);
    }
    finishTrenchConstruction() {
        this._trenchConstructionHandler.finish();
    }
    cancelTrenchConstruction() {
        this._trenchConstructionHandler.cancel();
    }
    startTrenchEdit(trench, callback) {
        this._trenchEditHandler.start(trench, callback);
    }
    finishTrenchEdit() {
        this._trenchEditHandler.end();
    }
    cancelTrenchEdit() {
        this._trenchEditHandler.cancel();
    }
    async updateTrenchSettings(trenchID, settings, rubbingDestination) {
        const trench = this._site.getTrench(trenchID);
        if (trench) {
            await trench.updatePathSettings(settings, rubbingDestination);
            this._trenchEditHandler.refreshSectionSelection();
        }
    }
    updateTrenchMaterial(trenchID, commonMaterial, cutMaterial) {
        const trench = this._site.getTrench(trenchID);
        if (!trench) {
            return;
        }
        trench.updateMaterial(commonMaterial.surfaceColor, cutMaterial.transparency, cutMaterial.color, cutMaterial.transparency);
    }
    checkTrenchUnderCreation() {
        return (this._trenchConstructionHandler.trench &&
            this._trenchConstructionHandler.trench.constructionState ===
                ConstructionState.UNDER_CONSTRUCTION);
    }
    getTrenchInfo(trenchID) {
        const trench = this._site.getTrench(trenchID);
        return trench ? trench.toJSON() : undefined;
    }
    getTrenchLinearData(trenchInfo) {
        const trench = this._site.getTrench(trenchInfo.trenchID);
        return trench ? trench.getLinearData(trenchInfo) : undefined;
    }
    async loadTrenchInfo(data, stationInterval) {
        const trench = await Trench.fromJSON(data, this._cesiumViewer, stationInterval);
        this._site.addTrench(trench);
        ActivityLogger.log(Activity.TRENCH_LOADED);
        return trench;
    }
    deleteTrench(trenchID) {
        this._site.deleteTrench(trenchID);
    }
    setTrenchVisibility(trenchID, show) {
        const trench = this._site.getTrench(trenchID);
        if (!trench) {
            return;
        }
        trench.setVisibility(show);
    }
    fitToTrench(trenchID) {
        const trench = this._site.getTrench(trenchID);
        if (!trench) {
            return;
        }
        trench.fitToTrench();
    }
    getLengthOfTrench(trenchID) {
        const trench = this._site.getTrench(trenchID);
        if (!trench) {
            return;
        }
        return trench.getLength();
    }
    getTrenchSlopeInfo(trenchID) {
        const trench = this._site.getTrench(trenchID);
        return trench ? trench.getSlopeInfo() : undefined;
    }
    getTrenchHeightInfo(trenchID) {
        const trench = this._site.getTrench(trenchID);
        if (!trench) {
            return undefined;
        }
        const heightInfo = trench.getHeightInfo();
        return heightInfo;
    }
    getTrenchHeightInfoOfSection(trenchID) {
        const trench = this._site.getTrench(trenchID);
        if (!trench) {
            return undefined;
        }
        const heightInfo = trench.getHeightInfo();
        return heightInfo;
    }
    // public getPolylineVolumeGeometry(trench: Trench): PolylineVolumeGeometry {
    //     return trench.getPolylineVolumeGeometry();
    // }
    getMeshGeometry(trench) {
        const geometry = trench.getGeometry();
        const uniqueGeometry = EarthBrainUtils.removeDuplicateVertices(geometry);
        if (uniqueGeometry.vertices.length > 0) {
            EarthBrainUtils.removeInvalidFacesFromGeometry(uniqueGeometry);
            return {
                vertices: uniqueGeometry.vertices,
                indices: uniqueGeometry.indices,
            };
        }
    }
    getTrenchPointLength(trench) {
        return trench.controlPointLength;
    }
    getTrenchPointIDFromIndex(trench, index) {
        return trench.getPointIDFromIndex(index);
    }
    /**
     * This function will check trench in _pathMap and calculate volume of particular Trench
     * @param trenchID ID of Trench whose volume to calculate
     * @returns Fill and Cut Volume calculated based on heightmap
     */
    async volumeCalculationForTrench(trench) {
        const data = this.getVolumeCalculationDataForTrench(trench);
        if ("sourceHeightMap" in data && data.sourceHeightMap === undefined) {
            throw new Error(`Height Map not found for ${trench.assetType} ${trench.associatedRubbingDestination.id}`);
        }
        else if ("specifiedPlaneAltitude" in data &&
            data.specifiedPlaneAltitude === undefined) {
            throw new Error(`Specified Plane Altitude is undefined`);
        }
        //if volume entities in data are above
        return await VolumeCalculationUtils.calculateVolume(data);
    }
    getVolumeCalculationDataForTrench(trench) {
        let assetType;
        let data;
        switch (trench.associatedRubbingDestination.target) {
            case ERubbingDestination.POINT_CLOUD:
                assetType = AssetType.Point_Cloud;
                data = {
                    sourceHeightMap: this._site
                        .getTerrain(trench.associatedRubbingDestination.id, assetType)
                        .heightMap.getObject(),
                    targetHeightMap: trench.heightMap.getObject(),
                };
                break;
            case ERubbingDestination.DESIGN_DATA:
                assetType = AssetType.Design_Data;
                data = {
                    sourceHeightMap: this._site
                        .getTerrain(trench.associatedRubbingDestination.id, assetType)
                        .heightMap.getObject(),
                    targetHeightMap: trench.heightMap.getObject(),
                };
                break;
            case ERubbingDestination.FLAT_GROUND:
                assetType = AssetType.Flat_Ground_Data;
                data = {
                    sourceHeightMap: this._site
                        .getFlatGround(trench.associatedRubbingDestination.id)
                        .heightMap.getObject(),
                    targetHeightMap: trench.heightMap.getObject(),
                };
                break;
            case ERubbingDestination.SPECIFIED_ALTITUDE:
                assetType = AssetType.UNKNOWN;
                data = {
                    specifiedPlaneAltitude: trench.associatedRubbingDestination.specifiedAltitude,
                    targetHeightMap: trench.heightMap.getObject(),
                };
                break;
            case ERubbingDestination.REFERENCE_TOPOLOGY:
                assetType = AssetType.REFERENCE_TOPOLOGY;
                data = {
                    sourceHeightMap: this._site
                        .getReferenceTopology(trench.associatedRubbingDestination.id)
                        .heightMap.getObject(),
                    targetHeightMap: trench.heightMap.getObject(),
                };
                break;
            default:
                break;
        }
        if (!assetType)
            return;
        return data;
    }
    getReferencePointElevation(trenchId) {
        const trench = this._site.getTrench(trenchId);
        if (trench) {
            return trench.referencePointElevation;
        }
    }
    averageHeightOfFirstAndLastPoint(trenchId) {
        const trench = this._site.getTrench(trenchId);
        if (trench) {
            return trench.averageHeightOfFirstAndLastPoint;
        }
    }
}
//# sourceMappingURL=trench-handler.js.map